import { AiAssistantInput } from "@/services/fobizzToolsApi";
import { AppNamespaceEnum } from "@/services/fobizzToolsApi";

const PAGINATION_LIMIT = 10;

export function useAssistantQueries() {
  const queryClient = useQueryClient();
  const { aiAssistants } = useApiClient();

  const isLoading = useIsFetching({
    queryKey: [AppNamespaceEnum.AI_ASSISTANT],
  });

  const parseInternationalValue = (international?: Ref<string>) => {
    return international?.value === "true" ? true : false;
  };

  const useInfiniteSearchQuery = ({
    page,
    limit,
    query = ref(""),
    category,
    international,
    pupils,
  }: {
    page?: Ref<number>;
    limit?: number;
    query?: Ref<string>;
    category?: Ref<string>;
    international?: Ref<string>;
    pupils?: boolean;
  }) => {
    return useInfiniteQuery(
      {
        queryKey: [
          AppNamespaceEnum.AI_ASSISTANT,
          "infinite",
          page,
          query,
          category,
          international,
        ],
        queryFn: ({ pageParam }) => {
          return aiAssistants.searchAiAssistants({
            page: pageParam,
            limit: limit || PAGINATION_LIMIT,
            query: query.value,
            category: category?.value,
            international: parseInternationalValue(international),
            pupils: pupils,
          });
        },
        initialPageParam: 1,
        getNextPageParam: (lastPage, _pages) => lastPage.nextPage,
      },
      queryClient
    );
  };

  const useIndexQuery = ({
    page,
    limit,
    category,
  }: {
    page?: Ref<number>;
    limit?: number;
    category?: Ref<string>;
  }) => {
    return useQuery(
      {
        queryKey: [AppNamespaceEnum.AI_ASSISTANT, "index", page, category],
        queryFn: () => {
          return aiAssistants.listAiAssistants({
            page: (page && page.value) || 1,
            limit: limit || PAGINATION_LIMIT,
            category: category?.value,
          });
        },
        placeholderData: keepPreviousData,
      },
      queryClient
    );
  };

  const useIndexInfiniteQuery = (params: {
    page?: Ref<number>;
    limit?: number;
    query?: Ref<string>;
  }) => {
    return useInfiniteQuery(
      {
        queryKey: [
          AppNamespaceEnum.AI_ASSISTANT,
          "index",
          "infinite",
          params.page,
          params.query,
        ],
        queryFn: ({ pageParam }) => {
          return aiAssistants.listAiAssistants({
            page: pageParam,
            limit: params.limit || PAGINATION_LIMIT,
            query: params.query?.value,
          });
        },
        initialPageParam: 1,
        getNextPageParam: (lastPage, _pages) => lastPage.nextPage,
      },
      queryClient
    );
  };

  const useSearchQuery = ({
    page,
    limit,
    query = ref(""),
    category,
    international,
    notCategory,
    internal,
  }: {
    page?: Ref<number>;
    limit?: number;
    query?: Ref<string>;
    category?: Ref<string>;
    international?: Ref<string>;
    notCategory?: string;
    internal?: boolean;
  }) => {
    return useQuery(
      {
        queryKey: [
          AppNamespaceEnum.AI_ASSISTANT,
          "search",
          page,
          query,
          category,
          international,
          internal,
        ],
        queryFn: () => {
          return aiAssistants.searchAiAssistants({
            page: (page && page.value) || 1,
            limit: limit || PAGINATION_LIMIT,
            query: query.value,
            category: category?.value,
            international: parseInternationalValue(international),
            ...(notCategory && { notCategory }),
            ...(internal && { internal }),
          });
        },
        placeholderData: keepPreviousData,
      },
      queryClient
    );
  };

  const useRecentlyUsedQuery = ({
    page,
    limit,
  }: {
    page?: Ref<number>;
    limit?: number;
  }) => {
    return useQuery(
      {
        queryKey: [AppNamespaceEnum.AI_ASSISTANT, "recentlyUsed", page],
        queryFn: () => {
          return aiAssistants.recentlyUsedAiAssistants({
            page: (page && page.value) || 1,
            limit: limit || PAGINATION_LIMIT,
          });
        },
        placeholderData: keepPreviousData,
      },
      queryClient
    );
  };

  const useFeaturedQuery = (
    {
      page,
      limit,
      international,
    }: {
      page?: Ref<number>;
      limit?: number;
      international?: Ref<string>;
    },
    opts = {}
  ) => {
    return useQuery(
      {
        queryKey: [
          AppNamespaceEnum.AI_ASSISTANT,
          "featured",
          page,
          limit,
          international,
        ],
        queryFn: () => {
          return aiAssistants.listFeaturedAiAssistants({
            page: (page && page.value) || 1,
            limit: limit || PAGINATION_LIMIT,
            international: international?.value,
          });
        },
        placeholderData: keepPreviousData,
        ...opts,
      },
      queryClient
    );
  };

  const getQuery = (id: Ref<string>, opts = {}) => {
    return useQuery(
      {
        queryKey: [AppNamespaceEnum.AI_ASSISTANT, id],
        queryFn: () => aiAssistants.getAiAssistant({ id: id.value }),
        ...opts,
      },
      queryClient
    );
  };

  const useDeleteMutation = () => {
    return useMutation(
      {
        mutationFn: (id: string) => aiAssistants.deleteAiAssistant({ id }),
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [AppNamespaceEnum.AI_ASSISTANT],
          });
        },
      },
      queryClient
    );
  };

  const useCreateMutation = () => {
    return useMutation(
      {
        mutationFn: (attrs: Partial<AiAssistantInput>) =>
          aiAssistants.createAssistant({ requestBody: { assistant: attrs } }),
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [AppNamespaceEnum.AI_ASSISTANT],
          });
        },
      },
      queryClient
    );
  };

  const useUpdateMutation = (id: string) => {
    return useMutation(
      {
        mutationFn: (attrs: Partial<AiAssistantInput>) =>
          aiAssistants.updateAiAssistant({
            id: id,
            requestBody: { assistant: attrs },
          }),
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [AppNamespaceEnum.AI_ASSISTANT],
          });
        },
      },
      queryClient
    );
  };

  const useArchiveMutation = (id: string) => {
    return useMutation(
      {
        mutationFn: () => aiAssistants.archiveAiAssistant({ id }),
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [AppNamespaceEnum.AI_ASSISTANT],
          });
        },
      },
      queryClient
    );
  };

  return {
    useIndexQuery,
    useIndexInfiniteQuery,
    useSearchQuery,
    useInfiniteSearchQuery,
    useRecentlyUsedQuery,
    getQuery,
    useCreateMutation,
    useUpdateMutation,
    useDeleteMutation,
    useArchiveMutation,
    isLoading,
    useFeaturedQuery,
  };
}
